<script setup>
  import { computed, onMounted, onUnmounted, ref } from 'vue';

  import HomeButton from '@/components/home/HomeButton.vue';

  import { HOME_HERO } from '@/constants/home';

  import addressHouse from '@/assets/images/home/hero-address-house.webp';
  import addressPin from '@/assets/images/home/hero-address-pin.svg';
  import collage from '@/assets/images/home/hero-collage.webp';
  import roofArrow from '@/assets/images/home/hero-roofer-check.svg';
  import starEmpty from '@/assets/images/home/star-empty.svg';
  import star from '@/assets/images/home/star.svg';

  defineEmits(['get-price']);

  const SAVINGS_DELAY_MS = 850;
  const SAVINGS_COUNT_MS = 900;

  const [, savingsNumber, savingsSuffix] = HOME_HERO.savings.amount.match(/^(\d+)(.*)$/);
  const savingsTarget = Number(savingsNumber);
  const savingsShown = ref(savingsTarget);
  const savingsText = computed(() => `${savingsShown.value}${savingsSuffix}`);
  let savingsFrame = null;

  onMounted(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    savingsShown.value = 0;
    const startAt = performance.now() + SAVINGS_DELAY_MS;
    const tick = (now) => {
      const progress = Math.min(Math.max((now - startAt) / SAVINGS_COUNT_MS, 0), 1);
      savingsShown.value = Math.round((1 - (1 - progress) ** 3) * savingsTarget);
      if (progress < 1) savingsFrame = requestAnimationFrame(tick);
    };
    savingsFrame = requestAnimationFrame(tick);
  });

  onUnmounted(() => cancelAnimationFrame(savingsFrame));
</script>

<template>
  <section
    id="hero"
    class="lg:from-pale-sky/0 lg:to-pale-sky relative w-full pt-18 md:pt-21 lg:bg-linear-to-b lg:pt-25"
  >
    <div
      class="mx-auto flex w-full max-w-299 flex-col items-center px-5 pt-8 lg:flex-row lg:items-start lg:justify-between lg:px-8 lg:pt-12 lg:pb-32 xl:px-0"
    >
      <div
        class="flex flex-col items-center text-center lg:w-[44.6%] lg:items-start lg:pt-6 lg:text-left"
      >
        <h1
          class="text-ink max-w-90 text-[34px]/10 font-bold sm:max-w-100 sm:text-[40px]/12 md:max-w-120 md:text-[48px]/14 lg:max-w-none lg:text-[44px]/13 xl:text-[56px]/16"
        >
          {{ HOME_HERO.title }}
        </h1>

        <div
          class="text-ink mt-8 max-w-85 text-[13px]/4 font-medium sm:max-w-110 sm:text-[15px]/5.5 md:max-w-121 md:text-[16px]/6 lg:mt-10 lg:text-[18px]/6.5"
        >
          <p>{{ HOME_HERO.description }}</p>
          <p class="mt-4 font-bold md:mt-6 lg:mt-6.5">{{ HOME_HERO.note }}</p>
        </div>

        <HomeButton class="mt-8" @click="$emit('get-price')">{{ HOME_HERO.cta }}</HomeButton>
      </div>

      <div class="relative mt-9 w-full pb-24 lg:mt-0 lg:mr-[1.5%] lg:w-[46%] lg:pb-0">
        <div
          class="from-pale-sky/0 to-pale-sky absolute -inset-x-5 top-16 bottom-0 bg-linear-to-b lg:hidden"
          aria-hidden="true"
        />

        <div class="collage relative mx-auto w-[min(100%-1.75rem,460px)] md:w-130 lg:w-full">
          <img
            :src="collage"
            width="1101"
            height="1102"
            :alt="HOME_HERO.imageAlt"
            fetchpriority="high"
            class="relative block h-auto w-full"
          />

          <div class="card address-card hero-pop" style="--pop-delay: 300ms" aria-hidden="true">
            <img :src="addressPin" alt="" class="address-pin" />
            <span class="card-label address-label">{{ HOME_HERO.addressLabel }}</span>
            <span class="address-bar" style="top: calc(var(--design-px) * 84)" />
            <span class="address-bar" style="top: calc(var(--design-px) * 102)" />
            <span class="address-bar address-bar-short" style="top: calc(var(--design-px) * 120)" />
            <img :src="addressHouse" alt="" class="address-house" />
          </div>

          <div
            v-for="roofer in HOME_HERO.roofers"
            :key="roofer.name"
            :class="[
              'card roofer-card hero-pop',
              roofer.selected ? 'roofer-selected' : 'roofer-other',
            ]"
            :style="{ '--pop-delay': roofer.selected ? '650ms' : '500ms' }"
            aria-hidden="true"
          >
            <img :src="roofer.avatar" alt="" class="roofer-avatar" />
            <div class="flex min-w-0 flex-col">
              <span class="card-label">{{ HOME_HERO.rooferLabel }}</span>
              <span class="roofer-name">{{ roofer.name }}</span>
              <span class="roofer-stars">
                <img v-for="n in 5" :key="n" :src="n <= roofer.rating ? star : starEmpty" alt="" />
              </span>
            </div>
            <img v-if="roofer.selected" :src="roofArrow" alt="" class="roofer-arrow" />
          </div>

          <div
            :style="{ '--pop-delay': `${SAVINGS_DELAY_MS}ms` }"
            class="hero-pop border-blue-light shadow-home-card absolute right-0 bottom-0 flex h-18 w-56 items-center justify-center gap-2 rounded-[10px] border-2 bg-white font-bold text-nowrap uppercase lg:top-[calc(var(--design-px)*216)] lg:right-[calc(var(--design-px)*-20)] lg:bottom-auto"
          >
            <span class="text-blue-light flex flex-col items-end text-[20px]/5">
              <span class="text-[24px]">{{ HOME_HERO.savings.label }}</span>
              <span>{{ HOME_HERO.savings.qualifier }}</span>
            </span>
            <!-- the hidden copy keeps the badge width while the number counts up -->
            <span class="text-cobalt grid text-[48px]/none">
              <span class="invisible col-start-1 row-start-1">{{ HOME_HERO.savings.amount }}</span>
              <span class="col-start-1 row-start-1 text-right tabular-nums" aria-hidden="true">
                {{ savingsText }}
              </span>
              <span class="sr-only">{{ HOME_HERO.savings.amount }}</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
  .collage {
    container-type: inline-size;
    --design-px: calc(100cqw / 552);
  }

  .card {
    position: absolute;
    border-radius: calc(var(--design-px) * 16);
    background: #fff;
    box-shadow: var(--shadow-home-card);
  }

  .card-label {
    color: var(--color-dim-gray);
    font-size: max(6px, calc(var(--design-px) * 10));
    font-weight: 700;
    line-height: 1.2;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  .address-card {
    top: calc(var(--design-px) * 24);
    left: calc(var(--design-px) * 292);
    width: calc(var(--design-px) * 260);
    height: calc(var(--design-px) * 172);
    border: 2px solid var(--color-blue-light);
  }

  .address-pin {
    position: absolute;
    top: calc(var(--design-px) * 24);
    left: calc(var(--design-px) * 20);
    width: calc(var(--design-px) * 16);
  }

  .address-label {
    position: absolute;
    top: calc(var(--design-px) * 60);
    left: calc(var(--design-px) * 20);
  }

  .address-bar {
    position: absolute;
    left: calc(var(--design-px) * 20);
    width: calc(var(--design-px) * 72);
    height: calc(var(--design-px) * 10);
    background: var(--color-gainsboro);
  }

  .address-bar-short {
    width: calc(var(--design-px) * 36);
  }

  .address-house {
    position: absolute;
    top: calc(var(--design-px) * 20);
    left: calc(var(--design-px) * 108);
    width: calc(var(--design-px) * 132);
    height: calc(var(--design-px) * 132);
    object-fit: cover;
  }

  .roofer-card {
    display: flex;
    align-items: center;
    gap: calc(var(--design-px) * 16);
    padding-left: calc(var(--design-px) * 20);
  }

  .roofer-selected {
    top: calc(var(--design-px) * 268);
    left: calc(var(--design-px) * -16);
    width: calc(var(--design-px) * 280);
    height: calc(var(--design-px) * 92);
    border: 2px solid var(--color-blue-light);
  }

  .roofer-other {
    top: calc(var(--design-px) * 328);
    left: calc(var(--design-px) * 24);
    width: calc(var(--design-px) * 272);
    height: calc(var(--design-px) * 88);
  }

  .roofer-avatar {
    width: calc(var(--design-px) * 64);
    height: calc(var(--design-px) * 64);
    flex-shrink: 0;
    border-radius: 9999px;
    object-fit: cover;
  }

  .roofer-name {
    margin-top: calc(var(--design-px) * 4);
    overflow: hidden;
    color: #000;
    font-size: max(10px, calc(var(--design-px) * 16));
    font-weight: 600;
    line-height: 1.2;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .roofer-stars {
    display: flex;
    gap: calc(var(--design-px) * 8);
    margin-top: calc(var(--design-px) * 8);
  }

  .roofer-stars img {
    width: calc(var(--design-px) * 12);
  }

  /* the overlays pop in one by one after load; the photo and copy show at once */
  .hero-pop {
    animation: hero-pop 0.5s ease-out var(--pop-delay, 0ms) both;
  }

  @keyframes hero-pop {
    from {
      opacity: 0;
      transform: translateY(12px) scale(0.96);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .hero-pop {
      animation: none;
    }
  }

  .roofer-arrow {
    position: absolute;
    top: calc(var(--design-px) * 16);
    right: calc(var(--design-px) * 16);
    width: calc(var(--design-px) * 10);
  }
</style>
